import { Card, CardActions, CardContent, Grid, Typography, useEventCallback } from '@mui/material';
import { useFormik } from 'formik';
import { PmiButton } from 'shell/components/shared/button';
import { ReactElement, useCallback } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { DocumentCitizenshipUploadFormVM } from 'civil-status/view-models/document-citizenship-form';
import MatricolaList from '../shared/matricola-list';
import ListGender from 'shell/components/shared/gender';
import { DatePicker } from '@mui/lab';
import { PmiInputFile } from 'shell/components/shared/file-input';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { HttpResponse } from 'shell/models/http-response';
import { ConvertedImage } from 'shell/models/convert-image';
import { __CIVIL_STATUS_API_URL__ } from 'shell/constants/environment';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { StickyDiv } from 'shell/components/shared/sticky-div';
import { Cropper } from 'shell/components/shared/cropper';
import SelectRegistry from '../shared/select-registry';
import * as yup from 'yup';
import { commonSchemaObject } from 'civil-status/constants/common-schema';
import { citizenshipSchemaObject } from '../../../shell/constants/create-schema-citizenship';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { portion, ...rest } = commonSchemaObject;

const validationSchema = yup.object().shape({
  ...citizenshipSchemaObject,
  ...rest,
});

interface FormDocumentCitizenshipUploadNewProps {
  onOk: (form: DocumentCitizenshipUploadFormVM) => void;
  onCancel: (form?: DocumentCitizenshipUploadFormVM) => void;
  title: string;
  children?: ReactElement;
  disabled?: boolean;
  isEdit?: boolean;
}

const FormDocumentCitizenshipUploadNew = ({
  onOk,
  onCancel,
  title,
  disabled = false,
}: FormDocumentCitizenshipUploadNewProps): React.ReactElement => {
  const onSubmit = useCallback(
    (formValues) => {
      onOk(formValues);
    },
    [onOk]
  );

  const form = useFormik<DocumentCitizenshipUploadFormVM>({
    initialValues: {
      year: '',
      registerNumber: '',
      documentNumber: '',
      portion: '',
      serie: '',
      matricola: '',
      surname: '',
      name: '',
      gender: '',
      birthday: null,
      birthdayMunicipality: '',
      residenceMunicipality: '',
      citizenshipDate: null,
    },
    onSubmit,
    enableReinitialize: true,
    validateOnMount: true,
    validationSchema,
  });

  const convertDocument = useHttpClient<HttpResponse<ConvertedImage>>(
    `${__CIVIL_STATUS_API_URL__}/convert-image`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
      },
    }
  );

  const handleAddFile = useEventCallback((file: File) => {
    const reader = new FileReader();
    reader.addEventListener('load', (event) => {
      const { result } = event.target || {};
      (
        convertDocument({
          data: {
            formato: 'png',
            immagineIniziale: (result as string)?.split(',')[1],
          },
        }) as ResponsePromise<HttpResponse<ConvertedImage>>
      ).then((response) => {
        form.setFieldValue('upload', {
          file,
          raw: response?.data?.payload?.immagineConvertita
            ? (response?.data?.payload?.immagineConvertita as Array<string>)?.map(
                (immagine: string) => `data:image/png;base64,${immagine}`
              )
            : [],
          rawTiff: result as string,
        });
      });
    });

    reader.readAsDataURL(file);
  });

  return (
    <Card raised className="pmi-my-3">
      <form onSubmit={form.handleSubmit}>
        <CardContent style={{ padding: '24px' }}>
          <Typography gutterBottom variant="h5" component="h2">
            {title}
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Grid item xs={12} container spacing={2} style={{ paddingTop: '0px' }}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    label="Anno"
                    aria-label="Anno"
                    id="year"
                    fullWidth
                    inputProps={{
                      maxLength: '4',
                    }}
                    onChange={(event) => {
                      form.setFieldValue('year', event?.target.value);
                    }}
                    value={form.values.year || ''}
                    helperText={form.errors.year}
                    error={form.touched.year && Boolean(form.errors.year)}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} container spacing={2} style={{ paddingTop: '0px' }}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    label="Atto"
                    aria-label="Atto"
                    fullWidth
                    id="document"
                    onChange={(event) => {
                      form.setFieldValue('documentNumber', event?.target.value);
                    }}
                    inputProps={{
                      maxLength: '4',
                    }}
                    value={form.values.documentNumber || ''}
                    helperText={form.errors.documentNumber}
                    error={form.touched.documentNumber && Boolean(form.errors.documentNumber)}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} container spacing={2} style={{ paddingTop: '0px' }}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <SelectRegistry
                    isSmallSize
                    label="Registro"
                    id="registry"
                    onChange={(event) => {
                      form.setFieldValue('registerNumber', event?.target.value);
                    }}
                    value={form.values.registerNumber || ''}
                    error={form.touched.registerNumber && Boolean(form.errors.registerNumber)}
                  />
                </Grid>
              </Grid>
              {/* <Grid item xs={12} container spacing={2} style={{ paddingTop: '0px' }}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    label="Parte"
                    aria-label="Parte"
                    fullWidth
                    id="portion"
                    onChange={(event) => {
                      form.setFieldValue('portion', event?.target.value);
                    }}
                    inputProps={{
                      maxLength: '2',
                    }}
                    value={form.values.portion || ''}
                    helperText={form.errors.portion}
                    error={form.touched.portion && Boolean(form.errors.portion)}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} container spacing={2} style={{ paddingTop: '0px' }}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <SelectSerie
                    isSmallSize
                    label="Serie"
                    id="serie"
                    onChange={(event) => {
                      form.setFieldValue('serie', event?.target.value);
                    }}
                    value={form.values.serie || ''}
                    error={form.touched.serie && Boolean(form.errors.serie)}
                  />
                </Grid>
              </Grid> */}
              <Grid item xs={12} style={{ paddingTop: '10px' }}>
                <MatricolaList
                  isSmallSize
                  aria-label="Matricola"
                  label="Matricola"
                  id="matricola"
                  surname={form.values?.surname || ''}
                  name={form.values?.name || ''}
                  dateBirth={form.values?.birthday || null}
                  disabled={disabled}
                  value={form.values?.matricola || ''}
                  onChanges={(value) => form.setValues({ ...form.values, ...value })}
                  error={form.touched?.matricola && Boolean(form.errors?.matricola)}
                />
              </Grid>
              <Grid item xs={12} style={{ paddingTop: '10px' }}>
                <PmiTextField
                  aria-label="Cognome"
                  label="Cognome"
                  id="surname"
                  name="surname"
                  fullWidth
                  disabled={disabled}
                  value={form.values?.surname || ''}
                  onChange={(event) => form.setFieldValue('surname', event?.target.value)}
                  error={form.touched?.surname && Boolean(form.errors?.surname)}
                  helperText={form.errors?.surname}
                  inputProps={{
                    maxLength: '155',
                  }}
                />
              </Grid>
              <Grid item xs={12} style={{ paddingTop: '10px' }}>
                <PmiTextField
                  aria-label="Nome"
                  label="Nome"
                  id="name"
                  name="name"
                  fullWidth
                  disabled={disabled}
                  value={form.values?.name || ''}
                  onChange={(event) => form.setFieldValue('name', event?.target.value)}
                  error={form.touched?.name && Boolean(form.errors?.name)}
                  helperText={form.errors?.name}
                />
              </Grid>
              <Grid item xs={12} md={6} style={{ paddingTop: '10px' }}>
                <ListGender
                  isSmallSize
                  value={form.values?.gender || ''}
                  onChange={(event) => form.setFieldValue('gender', event?.target.value)}
                  error={form.touched?.gender && Boolean(form.errors?.gender)}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} md={6} style={{ paddingTop: '10px' }}>
                <DatePicker
                  label="Data nascita"
                  aria-label="Data nascita"
                  value={form.values?.birthday || null}
                  disabled={disabled}
                  onChange={(value) => form.setFieldValue('birthday', value)}
                  renderInput={(params) => (
                    <PmiTextField
                      id="birthday"
                      name="birthday"
                      {...params}
                      fullWidth
                      error={form.touched?.birthday && Boolean(form.errors?.birthday)}
                      helperText={form.errors?.birthday}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} style={{ paddingTop: '0px' }}>
                <span>F2 per Milano</span>
              </Grid>
              <Grid item xs={12} style={{ paddingTop: '10px' }}>
                <PmiTextField
                  aria-label="Comune nascita"
                  label="Comune nascita"
                  id="birthdayMunicipality"
                  name="birthdayMunicipality"
                  onChange={(event) =>
                    form.setFieldValue('birthdayMunicipality', event?.target.value)
                  }
                  onKeyDown={(event) => {
                    if (event.code === 'F2') {
                      event.preventDefault();
                      event.stopPropagation();
                      form.setFieldValue('birthdayMunicipality', 'MILANO');
                      return;
                    }
                  }}
                  value={form.values?.birthdayMunicipality || ''}
                  helperText={form.errors?.birthdayMunicipality}
                  error={
                    form.touched?.birthdayMunicipality && Boolean(form.errors?.birthdayMunicipality)
                  }
                  fullWidth
                  disabled={disabled}
                  inputProps={{
                    maxLength: '155',
                  }}
                />
                <span>F2 per Milano</span>
              </Grid>
              <Grid item xs={12} style={{ paddingTop: '10px' }}>
                <PmiTextField
                  aria-label="Comune residenza"
                  label="Comune residenza"
                  id="residenceMunicipality"
                  name="residenceMunicipality"
                  onChange={(event) =>
                    form.setFieldValue('residenceMunicipality', event?.target.value)
                  }
                  onKeyDown={(event) => {
                    if (event.code === 'F2') {
                      event.preventDefault();
                      event.stopPropagation();
                      form.setFieldValue('residenceMunicipality', 'MILANO');
                      return;
                    }
                  }}
                  value={form.values?.residenceMunicipality || ''}
                  helperText={form.errors?.residenceMunicipality}
                  error={
                    form.touched?.residenceMunicipality &&
                    Boolean(form.errors?.residenceMunicipality)
                  }
                  fullWidth
                  disabled={disabled}
                  inputProps={{
                    maxLength: '155',
                  }}
                />
              </Grid>
              <Grid item xs={12} style={{ paddingTop: '10px' }}>
                <DatePicker
                  label="Data cittadinanza"
                  aria-label="Data cittadinanza"
                  value={form.values?.citizenshipDate || null}
                  disabled={disabled}
                  onChange={(value) => form.setFieldValue('citizenshipDate', value)}
                  renderInput={(params) => (
                    <PmiTextField
                      id="citizenshipDate"
                      name="citizenshipDate"
                      {...params}
                      fullWidth
                      error={form.touched?.citizenshipDate && Boolean(form.errors?.citizenshipDate)}
                      helperText={form.errors?.citizenshipDate}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                Carica documento&nbsp;
                <PmiInputFile
                  accept="image/tiff"
                  multiple={false}
                  file={form.values.upload?.file}
                  showFilename
                  onChange={handleAddFile}
                  color="cdmBrandThird"
                  tipMaxSize={300}
                />
              </Grid>
            </Grid>
            <Grid item xs={12} md={8}>
              <StickyDiv>
                <Cropper
                  images={form.values.upload?.raw || []}
                  disabled={!form.values?.upload?.raw?.length}
                />
              </StickyDiv>
            </Grid>
          </Grid>
        </CardContent>
        <CardActions style={{ padding: '24px 24px 16px 24px' }}>
          <PmiButton variant="contained" color="primary" type="submit" disabled={disabled}>
            Conferma
          </PmiButton>
          <PmiButton
            variant="outlined"
            color="cdmBrand"
            onClick={() => {
              onCancel(form.values);
            }}
          >
            Annulla
          </PmiButton>
        </CardActions>
      </form>
    </Card>
  );
};

FormDocumentCitizenshipUploadNew.displayName = 'Form Document Birth new component';

export default FormDocumentCitizenshipUploadNew;
